import React, { useState } from "react"
import { Calendar, ArrowRight, Zap, Settings, BookOpen, User } from "lucide-react"
import { Link } from "react-router-dom"
import Header from "./Header"
import Footer from "./Footer"
import { Helmet } from 'react-helmet'



const AboutPage = () => {

  // ✅ Add these state hooks to support the Header
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const activeSection = "about"

  return (
    <div className="bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] text-white">
      <Helmet>
        <title>About – Myte Group | Ahmed Mekallach</title>
        <meta name="description" content="Meet Ahmed Mekallach, Systems Builder & Founder of Myte Group – Building the Post-AI Economy." />
        <link rel="canonical" href="https://mytegroup.ai/about" />
      </Helmet>

      {/* ✅ Shared Header */}
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} activeSection={activeSection} />

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:py-28">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              From Steel to Systems:
              <br /> Building the Infrastructure of the Post-AI Economy
            </h1>
            <p className="text-lg text-blue-200 max-w-2xl mb-6">
              Meet Ahmed Mekallach – Systems Builder, Strategic Advisor, and Founder of Myte Group
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <a
                href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-lg text-lg font-medium"
              >
                Schedule Discovery Call <Calendar className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/in/ahmedmekallach"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white text-white hover:bg-white hover:text-[#0e1d34] px-6 py-3 rounded-lg text-lg font-medium"
              >
                Connect on LinkedIn <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
          <div className="flex-1 max-w-md">
            <img src="/aboutpage/AhmedMekallachAbout.jpg" alt="Ahmed Mekallach" className="rounded-xl shadow-xl w-full" />
          </div>
        </div>
      </section>

      {/* Why I Build */}
      <section className="py-20 px-4 sm:px-6 bg-[#f0f4f9] text-[#0e1d34]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <img src="/aboutpage/AhmedHeart.jpg" alt="Why I Build" className="rounded-lg shadow-xl" />
          <div>
            <h2 className="text-3xl font-bold mb-4">Why I Build</h2>
            <p className="mb-4">
              My journey began on construction sites managing chaos with clipboards and whiteboards. Watching my father’s
              health decline reframed everything: freedom, clarity, and legacy became non-negotiables.
            </p>
            <p className="mb-4">
              Then GPT-4 happened. I saw the future. I realized we could close knowledge gaps, automate workflows,
              and build systems that empower—not replace—people.
            </p>
            <blockquote className="text-lg italic border-l-4 border-[#3080c1] pl-4 text-[#3080c1]">
              “We’re not here to code software. We’re here to restore autonomy.”
            </blockquote>
          </div>
        </div>
      </section>

      {/* Conference Impact */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Clarity at Scale</h2>
          <p className="text-blue-200 max-w-3xl mx-auto">
            When I spoke to over 1,200 ironworkers at IMPACT, I realized something: clarity moves people more than
            complexity ever will.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <img src="/aboutpage/Crowd1.jpg" className="rounded-lg shadow-lg" />
          <img src="/aboutpage/Crowd2.jpg" className="rounded-lg shadow-lg" />
          <img src="/aboutpage/RaiseYourHand.jpg" className="rounded-lg shadow-lg" />
        </div>
      </section>

      {/* Your Tech. Your Way. */}
      <section className="py-20 px-4 sm:px-6 bg-white text-[#0e1d34]">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-10 items-center">
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-4">Your Tech. Your Way.</h2>
            <p className="mb-4">
              SaaS traps you in monthly fees and inflexible workflows. Myte liberates you. I build tools that are
              OpenAI-powered, self-hosted, and fully yours.
            </p>
            <p className="mb-6">
              Everything I create is documented, explainable, trainable—and if you want to part ways, you’re never
              stuck. That’s digital autonomy.
            </p>
            <a
              href="/#solutions"
              className="inline-block px-6 py-3 bg-[#3080c1] text-white rounded-lg font-medium hover:bg-[#246399] transition"
            >
              Explore Solutions
            </a>
          </div>
          <div className="flex-1">
            <img src="/aboutpage/KHandAM.jpg" alt="KHandshake" className="rounded-xl shadow-lg" />
          </div>
        </div>
      </section>

      {/* The Myte Method */}
      <section className="py-20 px-4 sm:px-6 text-white bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34]">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold mb-6">The Myte Method</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {[
            { icon: Zap, title: "Discovery", desc: "We get radically clear on what matters." },
            { icon: Settings, title: "Integration", desc: "AI + workflows built around your people." },
            { icon: BookOpen, title: "Training", desc: "We teach your team to own and grow it." },
            { icon: User, title: "Ownership", desc: "You keep the code. You run the show." },
          ].map((step, idx) => (
            <div
              key={idx}
              className="bg-[#3080c1]/10 p-6 rounded-xl border border-[#3080c1]/30 shadow hover:shadow-lg hover:border-[#3080c1] transition"
            >
              <div className="mb-4 text-[#3080c1]">
                <step.icon className="w-10 h-10 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-blue-100">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Meet the Founder */}
      <section className="py-20 px-4 sm:px-6 bg-[#f0f4f9] text-[#0e1d34]">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/aboutpage/AMProfile.png" className="w-32 h-32 rounded-full mx-auto mb-6 border-4 border-[#3080c1]" />
          <h2 className="text-3xl font-bold mb-3">Ahmed Mekallach</h2>
          <p className="mb-4 text-lg text-gray-700">
            Systems Builder. OpenAI Strategist. Founder of Myte Group. Helping organizations move from overwhelm to
            ownership in a post-AI economy.
          </p>
          <div className="flex justify-center gap-4 mt-6">
            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-lg font-medium"
            >
              Book a 1:1 Call
            </a>
            <Link
              to="/videos"
              className="border border-[#3080c1] text-[#3080c1] hover:bg-[#3080c1] hover:text-white px-6 py-3 rounded-lg font-medium"
            >
              Watch Founder Videos
            </Link>
          </div>
        </div>
      </section>

       {/* ✅ Shared Footer*/}
      <Footer />
    </div>

  )
}

export default AboutPage
